import type { PermissionSet } from "../access/permissions"
import { hasPermission } from "../access/permissions"
import type { NavBadges } from "./nav-badges"
import type { NavSection } from "./nav-items"
import { NAV_SECTIONS } from "./nav-items"

/**
 * Пункт навигации в готовом к отрисовке виде.
 *
 * Считается ОДИН раз и одинаково для всех трёх раскладок: боковая навигация 200 px,
 * иконочная 56 px и нижние вкладки телефона получают один и тот же список. Иначе
 * администратор, открывший кабинет с телефона, увидит другой набор разделов, чем за
 * стойкой, — и решит, что у него отобрали права.
 */
export interface NavEntry {
  readonly section: NavSection
  readonly active: boolean
  /** Сколько записей требуют действия. undefined — ещё не знаем, и это не то же, что 0. */
  readonly badge: number | undefined
  /** Тихая точка: раздел просит внимания, но числом это не выражается. */
  readonly dot: boolean
}

function isActive(pathname: string, path: string): boolean {
  // Ровно совпадение или вложенный адрес. Простой startsWith сделал бы «/clients»
  // активным на «/clients-import», когда такой раздел появится.
  return pathname === path || pathname.startsWith(`${path}/`)
}

/**
 * Видимые разделы.
 *
 * ПОРЯДОК НЕ МЕНЯЕТСЯ (ТЗ §4.3 п.2). Раздел без права не уезжает вверх и не заменяется
 * соседом — он просто отсутствует, а остальные стоят на своих местах. У мастера
 * в навигации останется два-три пункта, и это нормальный, проверенный макетом вид,
 * а не поломка.
 */
export function buildNavEntries(params: {
  readonly permissions: PermissionSet
  readonly pathname: string
  readonly badges: NavBadges
  /**
   * Показывать ли точку на «Тариф и оплата».
   *
   * Решается снаружи и по одному правилу: точка нужна только тогда, когда полосы
   * «Оплата просрочена» на экране НЕТ. Полоса и точка говорят об одном и том же,
   * а одно и то же не сообщается дважды — иначе оба сигнала перестают замечать.
   */
  readonly billingDot: boolean
}): readonly NavEntry[] {
  return NAV_SECTIONS.filter(
    (section) => section.permission === null || hasPermission(params.permissions, section.permission),
  ).map((section) => ({
    section,
    active: isActive(params.pathname, section.path),
    badge: params.badges[section.id],
    dot: section.id === "billing" && params.billingDot,
  }))
}
